class CartDrawer extends HTMLElement {
  constructor() {
    super();
    window.FoxTheme = window.FoxTheme || {};
    window.FoxTheme.Cart = this;
    this.drawer = this.closest("drawer-component");
    window.FoxTheme.CartDrawer = this.drawer;
    this.openWhenAdded = this.dataset.openWhenAdded === "true";

    this.onCartRefreshListener = this.onCartRefresh.bind(this);
    this.onDrawerOpenedListener = this.onDrawerOpened.bind(this);
  }

  connectedCallback() {
    window.FoxThemeEvents.subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      async (state) => {
        if (state.item_count !== 0) {
          this.renderContents(
            state,
            true,
            state.source === "quick-order-list" ? false : true
          );
        }
      }
    );

    document.addEventListener("cart:refresh", this.onCartRefreshListener);
    /**
     * NOTE:
     * 配送日時アプリは「ドロワーが開いた後」の状態を前提に
     * 初期化されるケースがあるため、Drawer の open イベントにも追従する。
     */
    if (this.drawer) {
      this.drawer.addEventListener("open", this.onDrawerOpenedListener);
    }
  }

  disconnectedCallback() {
    document.removeEventListener("cart:refresh", this.onCartRefreshListener);
    if (this.drawer) {
      this.drawer.removeEventListener("open", this.onDrawerOpenedListener);
    }
  }

  onDrawerOpened() {
    /**
     * NOTE:
     * open 直後はDOM更新やトランジション直後で未安定なため、
     * ごく短い遅延後に配送日時再初期化通知を送る。
     */
    setTimeout(() => {
      this.notifyDeliveryDatetimeBlockUpdated();
    }, 80);
  }

  async onCartRefresh(event) {
    const cartDrawer = document.getElementById("cart-drawer");
    if (!cartDrawer) return;

    try {
      const response = await fetch(
        `${FoxThemeSettings.routes.root}?section_id=cart-drawer`
      );
      const responseText = await response.text();

      const parser = new DOMParser();
      const parsedHTML = parser.parseFromString(responseText, "text/html");

      const newCartContent = parsedHTML.getElementById("cart-drawer").innerHTML;
      cartDrawer.innerHTML = newCartContent;

      if (event.detail.open === true && !this.drawer.isOpen()) {
        this.drawer.openDrawer();
      }
    } catch (error) {
      console.error("Error refreshing cart:", error);
    }
  }

  renderContents(parsedState, renderFooter = true, isOpen = true) {
    if (parsedState.errors) return;
    
    // メッセージカードの入力内容を保存
    const savedMessages = this.saveMessageCardInputs();
    /**
     * NOTE:
     * 配送日時指定（AMPアプリ）は動的にDOMをマウントするため、
     * フッターの innerHTML 差し替えでアプリDOMを破棄すると
     * 再初期化されず空のコンテナだけが残ることがある。
     *
     * そのため、既存の配送日時ブロックを一時退避し、
     * フッター差し替え後に戻す。
     */
    const preservedDeliverySchedulingBlock = this.querySelector(
      "#f-cart-drawer__delivery-scheduling"
    );
    
    this.classList.contains("is-empty") && this.classList.remove("is-empty");
    
    // parsedState.sectionsが存在する場合のみDOMを更新
    if (parsedState.sections) {
      this.getSectionsToRender(renderFooter).forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        
        // sectionElementとsectionデータの両方が存在する場合のみ更新
        if (sectionElement && parsedState.sections[section.id]) {
          sectionElement.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.id],
            section.selector
          );

          // フッター更新時は配送日時ブロックを差し戻して、アプリDOMを維持する
          if (
            section.selector === ".f-drawer__footer" &&
            preservedDeliverySchedulingBlock
          ) {
            const newDeliverySchedulingBlock = sectionElement.querySelector(
              "#f-cart-drawer__delivery-scheduling"
            );
            if (newDeliverySchedulingBlock) {
              newDeliverySchedulingBlock.replaceWith(
                preservedDeliverySchedulingBlock
              );
            }
          }
        }
      });
    }

    // メッセージカードの入力内容を復元
    this.restoreMessageCardInputs(savedMessages);

    // メッセージカード入力機能を再初期化
    this.reinitializeMessageCardInputs();

    setTimeout(() => {
      if (this.openWhenAdded && !this.drawer.isOpen() && isOpen) {
        this.drawer.openDrawer();
        document.body.classList.add("prevent-scroll");
        window.FoxThemeEvents.emit(`ON_CART_DRAWER_OPEN`, parsedState);
      } else if (this.drawer.isOpen()) {
        /**
         * NOTE:
         * 既にドロワー表示中の更新（数量変更・削除など）は
         * open イベントが再発火しないため、ここで再通知する。
         */
        this.notifyDeliveryDatetimeBlockUpdated();
      }
    });
  }

  /**
   * 配送日時指定ブロック更新の通知を発火する。
   *
   * 実装メモ:
   * - アプリ実装差異に備え、複数のイベント名を通知する（後方互換を重視）
   * - 具体的な再描画ロジックはアプリ側に委譲し、テーマ側は「更新完了通知」に徹する
   */
  notifyDeliveryDatetimeBlockUpdated() {
    const deliveryDatetimeContainer = this.querySelector(
      "#cart-delivery-datetime_section"
    );
    if (!deliveryDatetimeContainer) return;

    // カート更新の完了を通知（汎用）
    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: {
          source: "cart-drawer",
        },
      })
    );

    // 配送日時ブロックの再描画契機として通知（アプリ実装差異を考慮）
    document.dispatchEvent(
      new CustomEvent("cart:delivery-datetime:refresh", {
        detail: {
          source: "cart-drawer",
          containerId: "cart-delivery-datetime_section",
        },
      })
    );

    document.dispatchEvent(
      new CustomEvent("amp:delivery-datetime:refresh", {
        detail: {
          source: "cart-drawer",
          containerId: "cart-delivery-datetime_section",
        },
      })
    );
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender(renderFooter = false) {
    const sections = [
      {
        id: "cart-drawer",
        selector: ".f-cart-drawer__items",
      },
    ];

    if (renderFooter) {
      sections.push({
        id: "cart-drawer",
        selector: ".f-drawer__footer",
      });
    }

    return sections;
  }

  // メッセージカードの入力内容を保存
  saveMessageCardInputs() {
    const savedMessages = {};
    const textareas = this.querySelectorAll('[data-message-card-textarea]');
    
    textareas.forEach(textarea => {
      const itemKey = textarea.dataset.messageCardItemKey;
      if (itemKey) {
        savedMessages[itemKey] = {
          value: textarea.value,
          cursorPosition: textarea.selectionStart
        };
      }
    });
    
    // LocalStorageにも保存
    this.saveToLocalStorage(savedMessages);
    
    return savedMessages;
  }

  // LocalStorageに保存
  saveToLocalStorage(messages) {
    try {
      localStorage.setItem('messageCardInputs', JSON.stringify(messages));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }

  // LocalStorageから読み込み
  loadFromLocalStorage() {
    try {
      const saved = localStorage.getItem('messageCardInputs');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return {};
    }
  }

  // メッセージカードの入力内容を復元
  restoreMessageCardInputs(savedMessages) {
    const textareas = this.querySelectorAll('[data-message-card-textarea]');
    
    textareas.forEach(textarea => {
      const itemKey = textarea.dataset.messageCardItemKey;
      if (itemKey) {
        // まず引数で渡された内容を確認
        let saved = savedMessages[itemKey];
        
        // 引数にない場合はLocalStorageから読み込み
        if (!saved) {
          const localStorageData = this.loadFromLocalStorage();
          saved = localStorageData[itemKey];
        }
        
        if (saved) {
          textarea.value = saved.value;
          textarea.setSelectionRange(saved.cursorPosition, saved.cursorPosition);
          
          // 文字数カウンターを更新
          const counter = textarea.parentElement.querySelector('[data-message-card-count]');
          if (counter) {
            counter.textContent = saved.value.length;
          }
        }
      }
    });
  }

  // メッセージカード入力機能を再初期化
  reinitializeMessageCardInputs() {
    const textareas = this.querySelectorAll('[data-message-card-textarea]');
    
    textareas.forEach(textarea => {
      // 既存のイベントリスナーを削除（バウンド関数を保存）
      if (textarea._messageCardInputHandlers) {
        textarea.removeEventListener('input', textarea._messageCardInputHandlers.input);
        textarea.removeEventListener('blur', textarea._messageCardInputHandlers.blur);
      }
      
      // 新しいイベントリスナーを追加（バウンド関数を保存）
      const inputHandler = this.updateCharacterCount.bind(this);
      const blurHandler = this.saveMessageToCart.bind(this);
      
      textarea.addEventListener('input', inputHandler);
      textarea.addEventListener('blur', blurHandler);
      
      // バウンド関数を保存（後で削除するため）
      textarea._messageCardInputHandlers = {
        input: inputHandler,
        blur: blurHandler
      };
    });
  }

  // 文字数カウンターの更新
  updateCharacterCount(event) {
    const textarea = event.target;
    const counter = textarea.parentElement.querySelector('[data-message-card-count]');
    
    if (textarea && counter) {
      const currentLength = textarea.value.length;
      counter.textContent = currentLength;
      
      // 文字数制限に近づいたら色を変更
      if (currentLength >= 180) {
        counter.style.color = '#ff6b6b';
      } else if (currentLength >= 150) {
        counter.style.color = '#ffa726';
      } else {
        counter.style.color = 'inherit';
      }
      
      // リアルタイムでLocalStorageに保存
      this.saveCurrentInputToLocalStorage(textarea);
    }
  }

  // 現在の入力内容をLocalStorageに保存
  saveCurrentInputToLocalStorage(textarea) {
    const itemKey = textarea.dataset.messageCardItemKey;
    if (!itemKey) return;
    
    try {
      // 既存のデータを読み込み
      const existingData = this.loadFromLocalStorage();
      
      // 現在の入力内容を更新
      existingData[itemKey] = {
        value: textarea.value,
        cursorPosition: textarea.selectionStart
      };
      
      // LocalStorageに保存
      this.saveToLocalStorage(existingData);
    } catch (error) {
      console.error('Error saving current input to localStorage:', error);
    }
  }

  // メッセージをカートに保存
  async saveMessageToCart(event) {
    const textarea = event.target;
    if (!textarea) return;

    const message = textarea.value.trim();
    const itemKey = textarea.dataset.messageCardItemKey;
    
    if (!itemKey) return;
    
    try {
      // カートアイテムのキーから数量を取得
      const cartItem = document.querySelector(`[data-cart-item="${itemKey}"]`);
      if (!cartItem) return;
      
      // 行番号を取得（id="CartDrawer-Item-{line}" または id="CartItem-{line}" から）
      const itemId = cartItem.id;
      let line = null;
      if (itemId) {
        const match = itemId.match(/(?:CartDrawer-Item-|CartItem-)(\d+)/);
        if (match) {
          line = parseInt(match[1]);
        }
      }
      
      if (!line) {
        console.error('Could not determine line number for cart item');
        return;
      }
      
      // 現在の数量を取得（デフォルトは1）
      let quantity = 1;
      const quantityInput = cartItem.querySelector('input[name="updates[]"]');
      if (quantityInput) {
        quantity = parseInt(quantityInput.value) || 1;
      }
      
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity,
          properties: {
            'メッセージ': message
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      // メッセージ保存後はカート全体の再レンダリングは不要
      // メッセージは既にpropertiesとして保存されているため

    } catch (error) {
      console.error('Error saving message to cart:', error);
    }
  }

  // 全てのメッセージをカートに保存（順次実行で競合を回避）
  async saveAllMessagesToCart() {
    const textareas = this.querySelectorAll('[data-message-card-textarea]');
    
    // 順次実行して409 Conflictを回避
    for (const textarea of textareas) {
      const message = textarea.value.trim();
      const itemKey = textarea.dataset.messageCardItemKey;
      
      if (!itemKey) continue;
      
      // カートアイテムのキーから数量を取得
      const cartItem = document.querySelector(`[data-cart-item="${itemKey}"]`);
      if (!cartItem) continue;
      
      // 行番号を取得（id="CartDrawer-Item-{line}" または id="CartItem-{line}" から）
      const itemId = cartItem.id;
      let line = null;
      if (itemId) {
        const match = itemId.match(/(?:CartDrawer-Item-|CartItem-)(\d+)/);
        if (match) {
          line = parseInt(match[1]);
        }
      }
      
      if (!line) {
        console.error(`Could not determine line number for cart item with key ${itemKey}`);
        continue;
      }
      
      // 現在の数量を取得（デフォルトは1）
      let quantity = 1;
      const quantityInput = cartItem.querySelector('input[name="updates[]"]');
      if (quantityInput) {
        quantity = parseInt(quantityInput.value) || 1;
      }
      
      // リトライロジック付きで保存処理を実行
      let retries = 3;
      let success = false;
      
      while (retries > 0 && !success) {
        try {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              line: line,
              quantity: quantity,
              properties: {
                'メッセージ': message
              }
            })
          });

          if (response.ok) {
            success = true;
            // 次のリクエストの前に少し待機（競合を回避）
            if (retries < 3) {
              await new Promise(resolve => setTimeout(resolve, 200));
            }
          } else if (response.status === 409) {
            // 409 Conflictの場合は少し待ってからリトライ
            retries--;
            if (retries > 0) {
              await new Promise(resolve => setTimeout(resolve, 300));
            } else {
              console.error(`Failed to save message for item ${itemKey} after retries: 409 Conflict`);
              return false;
            }
          } else {
            throw new Error(`Failed to update cart for item ${itemKey}: ${response.status}`);
          }
        } catch (error) {
          retries--;
          if (retries > 0) {
            await new Promise(resolve => setTimeout(resolve, 300));
          } else {
            console.error(`Error saving message to cart for item ${itemKey}:`, error);
            return false;
          }
        }
      }
      
      // 各リクエストの間に短い遅延を追加（競合を回避）
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return true;
  }

  // LocalStorageをクリーンアップ
  clearLocalStorage() {
    try {
      localStorage.removeItem('messageCardInputs');
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }
}
customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  initLoading() {
    this.loading = new window.FoxTheme.AnimateLoading(
      document.querySelector("cart-drawer"),
      { overlay: document.querySelector("cart-drawer") }
    );
  }

  /**
   * ドロワー内の数量変更・削除では親 CartItems.renderContents を使わない。
   * 親はフッターを innerHTML 差し替えするだけで配送日時（AMP）DOM の退避がなく、
   * 空表示になる。その後の cartUpdate で CartDrawer.renderContents が走っても手遅れ。
   * 常に <cart-drawer> 側の renderContents（退避・再通知あり）に委譲する。
   */
  renderContents(parsedState) {
    const cartDrawerRoot = this.closest("cart-drawer");
    if (
      cartDrawerRoot &&
      typeof cartDrawerRoot.renderContents === "function"
    ) {
      cartDrawerRoot.renderContents(parsedState, true, true);
      return;
    }
    super.renderContents(parsedState);
  }

  getSectionsToRender() {
    return [
      {
        id: "Drawer-Cart",
        section: "cart-drawer",
        selector: ".f-cart-drawer__items",
      },
      {
        id: "Drawer-Cart",
        section: "cart-drawer",
        /**
         * NOTE:
         * 小計ブロック単位ではなく、ドロワーフッター全体を再描画する。
         * 配送日時指定ブロック（アプリ埋め込み）がフッター配下にあるため、
         * ここを部分更新すると表示/状態の不整合が発生しやすい。
         */
        selector: ".f-drawer__footer",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

// カートドロワーが開かれた時にメッセージカード入力機能を初期化
document.addEventListener('DOMContentLoaded', () => {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    // 初期化
    cartDrawer.reinitializeMessageCardInputs();
    
    // LocalStorageから入力内容を復元
    setTimeout(() => {
      cartDrawer.restoreMessageCardInputs({});
    }, 100);
  }
  
  // カートドロワーが開かれた時の再初期化
  document.addEventListener('cart-drawer:opened', () => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawer.reinitializeMessageCardInputs();
      
      // LocalStorageから入力内容を復元
      setTimeout(() => {
        cartDrawer.restoreMessageCardInputs({});
      }, 100);
    }
  });

  // チェックアウトフォーム送信時に全てのメッセージを保存
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    // カートドロワーのチェックアウトフォームかチェック
    if (form.id === 'CartDrawer' || form.querySelector('[data-checkout-button], button[name="checkout"]')) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        // デフォルトの送信をキャンセル
        event.preventDefault();
        
        // 全てのメッセージをカートに保存
        const success = await cartDrawer.saveAllMessagesToCart();
        
        if (success) {
          // LocalStorageをクリーンアップ
          cartDrawer.clearLocalStorage();
          
          // チェックアウトページにリダイレクト
          window.location.href = '/checkout';
        } else {
          // エラーが発生した場合はユーザーに通知
          alert('メッセージの保存中にエラーが発生しました。もう一度お試しください。');
        }
      }
    }
  });

  // ページ離脱時に現在の入力内容を保存
  window.addEventListener('beforeunload', () => {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const textareas = cartDrawer.querySelectorAll('[data-message-card-textarea]');
      textareas.forEach(textarea => {
        cartDrawer.saveCurrentInputToLocalStorage(textarea);
      });
    }
  });
});
